import { html } from "lit-html";
import { HauntedFunc } from "Shared/haunted/HooksHelpers";
import { getParsedProperty } from "Services/common";
import { AdminPageViewModel } from "./AdminPageViewModel";
import { useStationsSection } from "./useStationsSection";
import { useNavitaireSettingsSection } from "./useNavitaireSettingsSection";
import { useTimetableSection } from "./useTimetableSection";
import { useSitemapSection } from "./useSitemapSection";
import { useWidgetsSection } from "./useWidgetsSection";
import { usePagesSection } from "./usePagesSection";
import { useAdminMenu } from "./useAdminMenu";
import { useState } from "haunted";
import { DynamicSettings } from "./DynamicSettings";
import { useCultureNameSettings } from "./useCultureNameSettings";
import { useDefaultPromoCodeSection } from "./useDefaultPromoCodeSection";
import { useFareCacheSection } from "./useFareCacheSection";
import { useFeatureSwitchesSection } from "./useFeatureSwitchesSection";
import { useInsuranceTabSettings } from "./useInsuranceTabSettings";
import { usePromoCodeSettings } from "./usePromoCodeSettings";
import { useRouteSettings } from "./useRouteSettings";

export type CacheSectionId = "stations" | "navitaire-settings" | "timetable" | "sitemap" | "widgets" | "pages";
export type DynamicSettingSectionId =
	| "feature-switches"
	| "fare-cache"
	| "default-promo-code"
	| "insurance-tab"
	| "culture-names"
	| "dynamic-promo-codes"
	| "default-countries";

export const cacheMap = new Map<CacheSectionId, string>([
	["navitaire-settings", "Navitaire Settings"],
	["stations", "Stations"],
	["timetable", "Timetable"],
	["sitemap", "Sitemap"],
	["widgets", "Widgets"],
	["pages", "Pages"],
]);

export const dynamicSettingsMap = new Map<DynamicSettingSectionId, string>([
	["feature-switches", "Feature Switches"],
	["culture-names", "Culture Names"],
	["default-countries", "Default Countries"],
	["fare-cache", "Fare Cache"],
	["default-promo-code", "Default Promo Code"],
	["dynamic-promo-codes", "Dynamic Promo Codes"],
	["insurance-tab", "Insurance Tab"],
]);

export const observedAttributes: (keyof Attributes)[] = ["admin-props"];

export const name = "admin-page";

export interface Attributes {
	"admin-props": string;
}

export interface Props {
	adminProps: AdminPageViewModel;
}

export const Component: HauntedFunc<Props> = (host) => {
	const props: AdminPageViewModel =
		host.adminProps && typeof host.adminProps === "string"
			? getParsedProperty<AdminPageViewModel>(host.adminProps)
			: undefined;

	const [newSettings, setNewSettings] = useState<DynamicSettings>(props.DynamicSettings);

	const adminMenu = useAdminMenu({
		dynamicSettingsExpiresOn: props.DynamicSettingsExpiresOn,
		dynamicSettingsRefreshedOn: props.DynamicSettingsRefreshedOn,
		IsPreview: props.IsPreview,
	});

	const stationsSection = useStationsSection({
		isPreview: props.IsPreview,
		state: props.Stations,
	});
	const navitaireSettingsSection = useNavitaireSettingsSection({
		isPreview: props.IsPreview,
		state: props.NavitaireSettings,
	});
	const timetableSection = useTimetableSection();
	const sitemapSection = useSitemapSection({ isPreview: props.IsPreview, state: props.Sitemap });
	const widgetsSection = useWidgetsSection({
		homeWidgetsValues: props.HomeWidgetsValues,
		isPreview: props.IsPreview,
		state: props.HomeWidgetsCacheState,
	});
	const pagesSection = usePagesSection({
		isPreview: props.IsPreview,
		pagesValues: props.PagesValues,
	});
	const featureSwitches = useFeatureSwitchesSection({ newSettings, setNewSettings });
	const insurance = useInsuranceTabSettings({ newSettings, setNewSettings });
	const cultures = useCultureNameSettings({ newSettings, setNewSettings });
	const promoCodes = usePromoCodeSettings({ newSettings, setNewSettings });
	const routes = useRouteSettings({ newSettings, setNewSettings });
	const fareCache = useFareCacheSection({ newSettings, setNewSettings });
	const defaultPromoCodes = useDefaultPromoCodeSection({ newSettings, setNewSettings });

	const titleTemplate = () => {
		if (!adminMenu.currentPage) return "";

		const title =
			cacheMap.get(adminMenu.currentPage as CacheSectionId) ||
			dynamicSettingsMap.get(adminMenu.currentPage as DynamicSettingSectionId);
		return html`
			<h1 class="mb-2 mt-8 border-b border-solid border-n-blue">
				<span class="inline-block bg-n-blue p-2 text-lg font-bold text-white"> ${title} </span>
			</h1>
		`;
	};

	return html`
		${adminMenu.htmlTemplate()}
		<div class="fixed bottom-[60px] left-64 right-0 top-4 overflow-y-auto bg-white px-8">
			${titleTemplate()} ${adminMenu.currentPage === "stations" ? stationsSection.htmlTemplate() : ""}
			${adminMenu.currentPage === "navitaire-settings" ? navitaireSettingsSection.htmlTemplate() : ""}
			${adminMenu.currentPage === "timetable" ? timetableSection.htmlTemplate() : ""}
			${adminMenu.currentPage === "sitemap" ? sitemapSection.htmlTemplate() : ""}
			${adminMenu.currentPage === "widgets" ? widgetsSection.htmlTemplate() : ""}
			${adminMenu.currentPage === "pages" ? pagesSection.htmlTemplate() : ""}
			${adminMenu.currentPage === "feature-switches" ? featureSwitches.htmlTemplate() : ""}
			${adminMenu.currentPage === "culture-names" ? cultures.htmlTemplate() : ""}
			${adminMenu.currentPage === "fare-cache" ? fareCache.htmlTemplate() : ""}
			${adminMenu.currentPage === "default-promo-code" ? defaultPromoCodes.htmlTemplate() : ""}
			${adminMenu.currentPage === "insurance-tab" ? insurance.htmlTemplate() : ""}
			${adminMenu.currentPage === "dynamic-promo-codes" ? promoCodes.htmlTemplate() : ""}
			${adminMenu.currentPage === "default-countries" ? routes.htmlTemplate() : ""}
		</div>
	`;
};
